import { useTranslation } from "react-i18next";
import { localized, useAppLanguage } from "../utils/localized";

import { BasketItem } from "../store/slices/basketSlice";
import { getBasketItemUnitPrice } from "../store/selectors/basket";
import { ReplacementEntry } from "../modules/basket/libs/replacementLog";
import { Drawer, DrawerContent, DrawerTitle } from "./ui/drawer";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { XIcon } from "../assets/X";
import { DeleteIcon } from "../assets/Delete";
import { ArrowRightIcon } from "../assets/ArrowRight";
import { CheckIcon } from "../assets/Check";
import { cn } from "../lib/utils";
import { formatPrice } from "../utils/getPrice";
import { impactOccuer } from "../utils/hapticEvents";
import { ProductImage } from "./ProductImage";

type IProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  items: BasketItem[];
  replacements: ReplacementEntry[];
  /** The item currently being replaced, highlighted in the list. */
  activeCartKey?: string | null;
  onChange: (item: BasketItem) => void;
  /** Removes one item straight away — there is no confirmation step. */
  onRemove: (item: BasketItem) => void;
  /** Removes everything still unavailable, straight away. */
  onRemoveAll: () => void;
};

export const UnavailableItemsDrawer = ({
  open,
  setOpen,
  items,
  replacements,
  activeCartKey,
  onChange,
  onRemove,
  onRemoveAll,
}: IProps) => {
  const { t } = useTranslation();
  const lang = useAppLanguage();

  const title = (item: {
    title: string;
    title_uz: string;
    title_en?: string;
  }) => localized(lang, { ru: item.title, uz: item.title_uz, en: item.title_en });

  return (
    <Drawer shouldScaleBackground={false} open={open} onOpenChange={setOpen}>
      <DrawerContent className="bg-fill-background px-4 pb-safe-bottom">
        <div className="sticky top-0 z-10 bg-fill-background pt-3 pb-2">
          <div className="flex flex-row items-start justify-between gap-x-2">
            <DrawerTitle>
              <p className="text-display-md">{t("unavailable-title")}</p>
            </DrawerTitle>
            <button
              type="button"
              aria-label={t("cancel")}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-button-secondary active:opacity-70"
              onClick={() => setOpen(false)}
            >
              <XIcon className="text-text-icon-secondary" />
            </button>
          </div>
          {items.length > 0 && (
            <p className="pt-0.5 text-caption-sm-medium text-text-tertiary">
              {items.length}{" "}
              {items.length === 1 ? t("position-one") : t("position-many")}
            </p>
          )}
        </div>

        {/* No overflow of its own — DrawerContent already scrolls its children,
            and the header/footer stick to that scrollport. */}
        <div className="flex flex-col gap-y-4 pb-2">
          {items.length > 0 && (
            <div className="flex flex-col gap-y-1.5">
              <p className="px-1 text-caption-xs-semibold uppercase tracking-wide text-fill-danger">
                {t("not-available")} · {items.length}
              </p>
              {items.map((item) => (
                <Card
                  key={item.cartKey}
                  className={cn(
                    "flex flex-row items-center gap-x-2.5 rounded-xl bg-fill-base p-2.5 border border-transparent",
                    item.cartKey === activeCartKey && "border-border",
                  )}
                >
                  {/* Product shots are transparent PNGs — a tinted tile gives
                      them an edge instead of letting them float on the card. */}
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-fill-background">
                    <ProductImage
                      src={item.menu_image}
                      alt={item.title}
                      className="h-9 w-9 object-contain"
                    />
                  </div>

                  <div className="flex min-w-0 flex-1 flex-col gap-y-0.5">
                    <p className="text-caption-sm-medium line-clamp-1">
                      {title(item)}
                    </p>
                    <p className="text-caption-xs-semibold text-fill-danger">
                      {t("not-available")}
                    </p>
                    <p className="text-body-sm-semibold">
                      {formatPrice(getBasketItemUnitPrice(item))} {t("sum")}
                    </p>
                  </div>

                  <div className="flex shrink-0 flex-row items-center gap-x-1.5">
                    <Button
                      className="h-8 rounded-full bg-button-secondary px-3 shadow-none"
                      onClick={() => {
                        impactOccuer("light");
                        onChange(item);
                      }}
                    >
                      <p className="text-caption-sm-medium text-text-primary">
                        {t("change")}
                      </p>
                    </Button>
                    {/* Applies immediately — no staging, no confirm step. */}
                    <Button
                      className="h-8 w-8 shrink-0 rounded-full bg-button-secondary p-0 shadow-none"
                      onClick={() => {
                        impactOccuer("light");
                        onRemove(item);
                      }}
                    >
                      <DeleteIcon className="text-text-primary" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {replacements.length > 0 && (
            <div className="flex flex-col gap-y-1.5">
              <p className="px-1 text-caption-xs-semibold uppercase tracking-wide text-text-tertiary">
                {t("replaced")} · {replacements.length}
              </p>
              {replacements.map((entry) => (
                <Card
                  key={entry.id}
                  className="flex flex-row items-center gap-x-2 rounded-xl bg-fill-base p-2.5"
                >
                  <ProductImage
                    src={entry.from.menu_image}
                    alt={entry.from.title}
                    className="h-9 w-9 shrink-0 rounded-lg bg-fill-background object-contain p-1 opacity-45"
                  />
                  <p className="min-w-0 flex-1 truncate text-caption-sm-medium text-text-tertiary line-through">
                    {title(entry.from)}
                  </p>
                  <ArrowRightIcon className="h-3 w-3 shrink-0 text-text-icon-secondary" />
                  {/* Status lives on the new item's thumbnail as a badge
                      instead of its own row — the header already says
                      "replaced", repeating it per card was just noise. */}
                  <div className="relative shrink-0">
                    <ProductImage
                      src={entry.toImage}
                      alt={entry.toTitle}
                      className="h-9 w-9 rounded-lg bg-fill-background object-contain p-1"
                    />
                    <div className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-brand-green ring-2 ring-fill-base">
                      <CheckIcon className="h-[7px] w-[7px] text-text-white" />
                    </div>
                  </div>
                  <p className="min-w-0 flex-1 truncate text-caption-sm-medium">
                    {localized(lang, {
                      ru: entry.toTitle,
                      uz: entry.toTitleUz,
                      en: entry.toTitleEn,
                    })}
                  </p>
                </Card>
              ))}
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="sticky bottom-0 z-10 flex flex-col gap-y-2 bg-fill-background pt-2 pb-3">
            {/* Applies on tap like every other action here — nothing in this
                sheet asks twice. */}
            <Button
              className="h-10 w-full rounded-xl bg-button-secondary shadow-none"
              onClick={() => {
                impactOccuer("light");
                onRemoveAll();
              }}
            >
              <p className="text-caption-sm-medium text-fill-danger">
                {t("remove-all")}
              </p>
            </Button>
          </div>
        )}
      </DrawerContent>
    </Drawer>
  );
};

export default UnavailableItemsDrawer;
